'use client'

import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import AsyncCreatableSelect, {
  AsyncCreatableProps,
} from 'react-select/async-creatable'

import {
  components,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  InputActionMeta,
  MultiValue,
  MultiValueRemoveProps,
} from 'react-select'
import { ChevronDownIcon, CircleX, Loader2, SearchIcon } from 'lucide-react'
import { cn } from '@/lib/css-utils'

/**
 * isInteractive is used for E2E testing to determine when the dropdown
 * is ready for interaction. It adds a data-test attribute that testing
 * frameworks can use to verify the component's state.
 */
const DropdownContext = React.createContext<{ isInteractive?: boolean }>({
  isInteractive: false,
})

export interface DropdownHandleRef {
  clear: () => void
}

export interface DropdownOption {
  id: string // using ID to keep consistency with API
  label: string | React.JSX.Element
  searchKeys?: string[]
}

interface AsyncAutocompleteProps
  extends AsyncCreatableProps<
    DropdownOption,
    false,
    GroupBase<DropdownOption>
  > {
  placeholder?: string
  loadOptions?: (inputValue: string) => Promise<DropdownOption[]>
  options?: DropdownOption[]
  defaultOptions?: DropdownOption[]
  disableDefaultOptions?: boolean
  onChange: (selectedOptions: MultiValue<DropdownOption> | null) => void
  isMulti?: boolean
  isCreatable?: boolean
  isSearchable?: boolean
  isDisabled?: boolean
  value?: DropdownOption | MultiValue<DropdownOption> | null
  id: string
  noOptionsMessage?: (props: { inputValue: string }) => React.ReactNode
  formatCreateLabel?: (inputValue: string) => React.ReactNode
  onCreateOption?: (inputValue: string) => void
  isClearable?: boolean
  menuPortalTarget?: HTMLElement | null
  components?: React.ComponentProps<typeof AsyncCreatableSelect>['components']
  onBlur?: () => void
  styles?: React.ComponentProps<typeof AsyncCreatableSelect>['styles']
  closeMenuOnSelect?: boolean
  autoFocus?: boolean
  multiNoWrap?: boolean
  className?: string
  clearOnSelect?: boolean
  filterOptions?: (option: DropdownOption, inputValue: string) => boolean
  cacheOptions?: boolean
  isInteractive?: boolean
}

function isMultiValue(
  value: DropdownOption | MultiValue<DropdownOption> | null | undefined,
): value is MultiValue<DropdownOption> {
  return Array.isArray(value)
}

const stringifyLabel = (option: DropdownOption) => {
  if (typeof option.label === 'string' && option.searchKeys?.length) {
    return `${option.label} ${option.searchKeys.join(' ')}`
  }
  if (typeof option.label === 'string' && !option.searchKeys?.length) {
    return option.label
  }
  if (option.searchKeys?.length) {
    return option.searchKeys.join(' ')
  }
  return ''
}

const defaultSingleOptionLoader = async (
  options: DropdownOption[],
  inputValue: string,
): Promise<DropdownOption[]> => {
  return options.filter((option) => {
    return stringifyLabel(option)
      .toLowerCase()
      .includes(inputValue.toLowerCase())
  })
}

function IndicatorSeparator() {
  return null
}

function DropdownIndicator(props: DropdownIndicatorProps) {
  if (props.isMulti) {
    return <ChevronDownIcon className="h-4 w-4 text-copy-accent mr-2" />
  }
  return null
}

function ControlContainer({ children, ...props }: ControlProps) {
  const { isInteractive } = React.useContext(DropdownContext)
  return (
    <components.Control {...props}>
      {props.isMulti ? null : (
        <SearchIcon className="h-4 w-4 text-select-icon-text ml-2 react-select__icon" />
      )}
      {isInteractive ? <span data-test-is-interactive={isInteractive} /> : null}
      {children}
    </components.Control>
  )
}

function MultiValueRemove({ children, ...props }: MultiValueRemoveProps) {
  return (
    <components.MultiValueRemove {...props}>
      <CircleX className="h-4 w-4 text-select-chips-text rounded-none" />
      {children}
    </components.MultiValueRemove>
  )
}

function isDropdownOption(
  value: DropdownOption | MultiValue<DropdownOption> | null | undefined,
): value is DropdownOption {
  return !Array.isArray(value)
}

/**
 * @description A dropdown component that supports async loading of options.
 * There are 4 modes:
 * 1. Static options (a regular select)
 * 2. Searchable async options
 * 3. A version of #1 that is multi-select
 * 4. A version of #2 that is multi-select
 */
export const Dropdown = forwardRef(function Dropdown(
  {
    placeholder = 'Select',
    loadOptions,
    options,
    defaultOptions,
    onChange,
    isMulti,
    isCreatable = false,
    id,
    noOptionsMessage,
    isClearable = false,
    disableDefaultOptions = false,
    autoFocus = false,
    multiNoWrap = false,
    className,
    clearOnSelect = false,
    isSearchable = true,
    isInteractive = false,
    ...props
  }: AsyncAutocompleteProps,
  ref,
) {
  const [isLoading, setIsLoading] = useState(false)
  const [isMounted, setIsMounted] = useState(false)
  const [hasTouched, setHasTouched] = useState(false)
  const [inputValue, setInputValue] = useState(
    isDropdownOption(props.value) ? (props.value?.label ?? '') : '',
  )

  // Must be deleted once
  // https://github.com/JedWatson/react-select/issues/5459 is fixed.
  useEffect(() => setIsMounted(true), [])

  useEffect(() => {
    if (!isMultiValue(props.value)) {
      setInputValue(String(props.value?.label ?? ''))
    }
  }, [isMulti, props.value])

  const handleLoadOptions = async (inputValue: string) => {
    setIsLoading(true)
    if (!loadOptions) return []
    try {
      const options = await loadOptions(inputValue)
      setIsLoading(false)
      return options
    } catch (error) {
      setIsLoading(false)
      console.warn(error)
      return []
    }
  }

  const handleChange = useCallback(
    (newValue: DropdownOption | MultiValue<DropdownOption> | null) => {
      const value = isMultiValue(newValue) ? newValue : [newValue]
      const label = value[0]?.label
      if (!isMulti) {
        setInputValue(typeof label === 'string' ? label : '')
      } else if (clearOnSelect) {
        setInputValue('')
      }
      onChange(value as MultiValue<DropdownOption> | null)
    },
    [clearOnSelect, isMulti, onChange],
  )

  /**
   * https://github.com/JedWatson/react-select/issues/2296
   */
  const handleInputChange = (
    inputValue: string,
    actionMeta: InputActionMeta,
  ) => {
    if (actionMeta.action === 'input-change') {
      setInputValue(inputValue)
      setHasTouched(true)
      if (!inputValue) {
        onChange(null)
      }
    }
  }

  if (!isMounted) return null
  if (options && !loadOptions) {
    loadOptions = defaultSingleOptionLoader.bind(null, options)
  }

  if (props.value === undefined) {
    // undefined does not clear the input value (singleValue component).
    // we need to set it to null to clear the input value.
    props.value = null
  }

  let defaultOpts = defaultOptions ?? options ?? undefined
  if (disableDefaultOptions) {
    defaultOpts = undefined
  }

  return (
    <DropdownContext.Provider value={{ isInteractive }}>
      <AsyncCreatableSelect
        isMulti={isMulti}
        isClearable={isClearable || isMulti}
        id={id}
        cacheOptions
        onBlur={() => {
          if (!props.value) {
            // Clear the input value on blur when user didn't select any option,
            // so the default options can be the full list again without filtering
            setInputValue('')
          }
        }}
        defaultOptions={defaultOpts}
        loadOptions={handleLoadOptions}
        onChange={handleChange}
        placeholder={placeholder}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        ref={ref}
        styles={{
          //https://stackoverflow.com/a/63898744/2316012
          menuPortal: (base: StylePropertyMap) => ({ ...base, zIndex: 9999 }),
        }}
        classNamePrefix="react-select"
        isLoading={isLoading}
        components={{
          DropdownIndicator,
          IndicatorSeparator,
          Control: ControlContainer,
          MultiValueRemove,
          ...props.components,
        }}
        onInputChange={handleInputChange}
        inputValue={inputValue}
        isValidNewOption={() => isCreatable} // Prevent showing the create option
        loadingMessage={() => (
          <div className="flex items-center">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading...
          </div>
        )}
        noOptionsMessage={
          noOptionsMessage ||
          (({ inputValue }: { inputValue: string }) =>
            inputValue && hasTouched
              ? 'No options found'
              : 'Start typing to search...')
        }
        getOptionValue={({ id }: DropdownOption) => id}
        autoFocus={autoFocus}
        isSearchable={isSearchable && !isMulti}
        className={cn(
          className,
          multiNoWrap ? 'react-select__multi-no-wrap' : '',
        )}
        aria-label={placeholder}
        {...props}
      />
    </DropdownContext.Provider>
  )
})
